import { adSizes } from '../../lib/ad-sizes';
import { commercialFeatures } from '../../lib/commercial-features';
import { adSlotContainerClass, createAdSlot, wrapSlotInContainer, } from '../../lib/create-ad-slot';
import { getCurrentBreakpoint } from '../../lib/detect/detect-breakpoint';
import fastdom from '../../lib/fastdom-promise';
import { computeStickyHeights, insertHeightStyles } from '../sticky-inlines';
import { initCarrot } from './carrot-traffic-driver';
import { rules } from './rules';
import { spaceFiller } from './space-filler';
const articleBodySelector = '.article-body-commercial-selector';
const isPaidContent = window.guardian.config.page.isPaidContent;
/**
 * Get the classname for an ad slot container
 *
 * We add 2 to the index because these are always ads added in the second pass.
 *
 * e.g. the 0th container inserted in pass 2 will have suffix `-2` to match `inline2`
 *
 * @param i Index of winning paragraph
 * @returns The classname for container
 */
const getStickyContainerClassname = (i) => `${adSlotContainerClass}-${i + 2}`;
const insertSlotAtPara = async (para, name, type, classes, containerOptions = {}) => {
    const ad = createAdSlot(type, {
        name,
        classes,
    });
    const node = wrapSlotInContainer(ad, containerOptions);
    await fastdom.mutate(() => {
        if (para.parentNode) {
            para.parentNode.insertBefore(node, para);
        }
    });
    return ad;
};
/**
 * Decide whether we have enough space to add additional sizes for a given advert.
 * This function ensures we don't insert large height ads at the bottom of articles,
 * when there's not enough room.
 *
 * This prevents adverts at the bottom of articles pushing down content.
 */
const decideAdditionalSizes = async (winningPara, sizes, isLastInline) => {
    // If this ad isn't the last inline then return all additional sizes
    if (!isLastInline) {
        return sizes;
    }
    // Compute the vertical distance from the TOP of the winning para to the BOTTOM of the article body
    const distanceFromBottom = await fastdom.measure(() => {
        const paraTop = winningPara.getBoundingClientRect().top;
        const articleBodyBottom = document
            .querySelector(articleBodySelector)
            ?.getBoundingClientRect().bottom;
        return articleBodyBottom
            ? Math.abs(paraTop - articleBodyBottom)
            : undefined;
    });
    // Return all of the sizes that will fit in the distance to bottom
    return sizes.filter((adSize) => distanceFromBottom ? distanceFromBottom >= adSize.height : false);
};
const addDesktopInline1 = (fillSlot) => {
    // these are added here and not in size mappings because the inline[i] name
    // is also used on fronts, where we don't want outstream or tall ads
    const additionalSizes = {
        phablet: [adSizes.outstreamDesktop, adSizes.outstreamGoogleDesktop],
        desktop: [adSizes.outstreamDesktop, adSizes.outstreamGoogleDesktop],
    };
    const insertAd = async (paras) => {
        const slots = paras.slice(0, 1).map(async (para) => {
            const name = 'inline1';
            const slot = await insertSlotAtPara(para, name, 'inline', 'inline');
            await fillSlot(name, slot, additionalSizes);
        });
        await Promise.all(slots);
    };
    return spaceFiller.fillSpace(rules.desktopInline1, insertAd, {
        waitForImages: true,
        waitForInteractives: true,
        pass: 'inline1',
    });
};
/**
 * Inserts all inline ads on desktop except for inline1.
 */
const addDesktopRightRailAds = (fillSlot, isConsentless) => {
    const insertAds = async (paras) => {
        const stickyContainerHeights = await computeStickyHeights(paras, articleBodySelector);
        void insertHeightStyles(stickyContainerHeights.map((height, index) => [
            getStickyContainerClassname(index),
            height,
        ]));
        const slots = paras.slice(0, paras.length).map(async (para, i) => {
            const isLastInline = i === paras.length - 1;
            const containerClasses = getStickyContainerClassname(i) +
                ' offset-right ad-slot--offset-right ad-slot-container--offset-right';
            const containerOptions = {
                sticky: true,
                className: containerClasses,
            };
            // these are added here and not in size mappings because the inline[i] name
            // is also used on fronts, where we don't want outstream or tall ads
            const additionalSizes = {
                desktop: await decideAdditionalSizes(para, [adSizes.halfPage, adSizes.skyscraper], isLastInline),
            };
            const slot = await insertSlotAtPara(para, `inline${i + 2}`, 'inline', 'inline', containerOptions);
            return fillSlot(`inline${i + 2}`, slot, additionalSizes);
        });
        await Promise.all(slots);
    };
    return spaceFiller.fillSpace(rules.desktopRightRail(isConsentless), insertAds, {
        waitForImages: true,
        waitForInteractives: true,
        pass: 'subsequent-inlines',
    });
};
const additionalMobileAndTabletInlineSizes = (index) => {
    if (index === 1) {
        return {
            mobile: [adSizes.portraitInterstitial],
        };
    }
    else if (index === 2) {
        return {
            mobile: [
                adSizes.portraitInterstitial,
                adSizes.pubmaticInterscroller,
            ],
        };
    }
    return undefined;
};
const addMobileAndTabletInlineAds = (fillSlot, currentBreakpoint) => {
    const insertAds = async (paras) => {
        const slots = paras.map(async (para, i) => {
            //Mobile top-above-nav is the first ad in the body and the inline1 is the second ad in the body.
            //Tablet top-above-nav is above the website's navigation and the inline1 is the first ad in the body.
            const name = currentBreakpoint === 'mobile' && i === 0
                ? 'top-above-nav'
                : currentBreakpoint === 'tablet'
                    ? `inline${i + 1}`
                    : `inline${i}`;
            const type = currentBreakpoint === 'mobile' && i === 0
                ? 'top-above-nav'
                : 'inline';
            const slot = await insertSlotAtPara(para, name, type, 'inline');
            return fillSlot(name, slot, additionalMobileAndTabletInlineSizes(i));
        });
        await Promise.all(slots);
    };
    return spaceFiller.fillSpace(rules.mobileAndTabletInlines, insertAds, {
        waitForImages: true,
        waitForInteractives: true,
        pass: 'mobile-inlines',
    });
};
/**
 * Add inline slots to the article body
 * @param fillSlot A function to call that will fill the slot when each ad slot has been inserted,
 * these could be google display ads or opt opt consentless ads.
 */
const addInlineAds = (fillSlot, isConsentless) => {
    const currentBreakpoint = getCurrentBreakpoint();
    if (['mobile', 'tablet'].includes(currentBreakpoint)) {
        return addMobileAndTabletInlineAds(fillSlot, currentBreakpoint);
    }
    if (isPaidContent) {
        return addDesktopRightRailAds(fillSlot, isConsentless);
    }
    return addDesktopInline1(fillSlot).then(() => addDesktopRightRailAds(fillSlot, isConsentless));
};
/**
 * Init all the article body adverts, including `carrot`
 * @param fillAdSlot a function to fill the ad slots
 */
const init = async (fillAdSlot) => {
    if (!commercialFeatures.articleBodyAdverts) {
        return Promise.resolve();
    }
    await addInlineAds(fillAdSlot, false);
    await initCarrot();
};
export { init, addInlineAds };
